import { createStyles, FormControl, FormControlLabel, IconButton, makeStyles, Radio, RadioGroup, Theme } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import { ChartFilter } from '../ChartComponents';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { convertStringNumberToNumber, formatNumber, getLabels, getMaxTickLimit, getSuggestedMaxTick } from '../../helpers/ChartHelper';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { DrillDownType } from '../DrillDownType';
import { SelectedItemProps } from '../MattersByStatusChart';
import moment from 'moment';
// import moment from 'moment';

import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { ChartInfo } from '../../components/ChartInfo';
import VideoLibraryOutlinedIcon from '@material-ui/icons/VideoLibraryOutlined';
import { VideoModal } from '../../components/VideoModal';
import MTooltip from '@material-ui/core/Tooltip';

import { RV_CHART_CLASS, RV_CHART_COLORS, isChartDataEmpty, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvTooltipStyle, rvValueAxisStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { ChartStatTiles } from '../ChartStatTiles';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartEmptyState } from '../ChartEmptyState';
import TimelineOutlinedIcon from '@material-ui/icons/TimelineOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            textAlign: 'center',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        popover: {},
        paper: {},
        radioGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            padding: '20px 15px 0px 15px',
            '& .title': {
                fontSize: 16,
            },
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            '& .titleWrapper': {
                textAlign: 'center',
                display: 'inline-block',
                width: '100%',
                padding: '15px',
                '& .title': {
                    fontSize: '18px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .value': {
                    fontSize: '24px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .secondary-title': {
                    fontSize: '16px',
                    fontWeight: 400,
                    display: 'block',
                    marginBottom: '15px',
                },
            },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
        },
        title: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 0,
        },
        secondTitleDrilldown: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 0,
            cursor: 'pointer',
            userSelect: 'none',
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
        total: {
            fontSize: '2.5rem',
            padding: '10px 0',
            fontWeight: 600,
            cursor: 'pointer',
            userSelect: 'none',
        },
        matterSettled: {
            fontSize: '1rem',
            margin: '10px 0px',
            cursor: 'pointer',
            userSelect: 'none',
        },
        learnIcon: {
            color: 'rgb(0, 120, 212)'
        },
    }),
);

export enum LastCountEnum {
    WEEK,
    MONTH,
    PREV_MONTH,
    NEXT_MONTH,
    NEXT_6_MONTHS,
    NEXT_12_MONTHS,
    NEXT_3_YEARS,
}

interface SettlementsChartsProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
    infoUrl: string | null;
}

interface ChartState {
    last: LastCountEnum;
    amount: number[];
    grouping: string[];
    matters: number[];
    mattersWithoutSettlements: number;
    total: number;
    mattersSettled: number;
    format: string[];
    symbol: string;
}

export const FutureSettlementsCharts: React.FC<SettlementsChartsProps> = (props) => {
    const classes = useStyles();

    // tslint:disable-next-line:no-any
    const chartRef = React.useRef<any>();

    const [isVideoModalOpen, setIsVideoModalOpen] = useState<boolean>(false);

    // tslint:disable-next-line:no-any
    const { loading, error, data } = useQuery<FutureSettlementData>(FUTURE_SETTLEMENTS_CHART_QYERY, {
        variables: {
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
            responsibility: props.chartFilter?.responsibility,
        },
    });

    const [state, setState] = useState<ChartState>({
        last: LastCountEnum.WEEK,
        amount: [],
        grouping: [],
        matters: [],
        mattersWithoutSettlements: 0,
        total: 0,
        format: [],
        mattersSettled: 0,
        symbol: '$',
    });

    const currencySymbolQuery = useQuery<CurrencySymbolData>(CURRENCY_SYMBOL_QUERY);

    useEffect(() => {
        if (currencySymbolQuery.data && currencySymbolQuery.data.options && !currencySymbolQuery.loading && !currencySymbolQuery.error) {
            setState((prevState) => {
                return {
                    ...prevState,
                    symbol: currencySymbolQuery.data!.options.currencySymbol.symbol,
                };
            });
        }
        // tslint:disable-next-line: align
    }, [currencySymbolQuery.loading, currencySymbolQuery.error, currencySymbolQuery.data]);

    useEffect(() => {
        if (!loading) {
            setState((prevState) => {
                let amount: number[] = [];
                let grouping: string[] = [];
                let matters: number[] = [];
                let mattersWithoutSettlements: number = 0;
                let mattersSettled: number = 0;
                let total: number = 0;
                let periodString = 'This Week';
                let format: string[] = [];

                if (!error && data && data.statistics && data.statistics.matter && data.statistics.matter.futureSettlements) {
                    if (state.last === LastCountEnum.WEEK) {
                        mattersWithoutSettlements = data.statistics.matter.futureSettlements.thisWeek.summaries.mattersWithoutSettlements;
                        total = data.statistics.matter.futureSettlements.thisWeek.summaries.total;
                        mattersSettled = data.statistics.matter.futureSettlements.thisWeek.summaries.mattersSettled;

                        amount = data.statistics.matter.futureSettlements.thisWeek.settlements.map((s) => s.amount);
                        grouping = data.statistics.matter.futureSettlements.thisWeek.settlements.map((s) => s.grouping);
                        matters = data.statistics.matter.futureSettlements.thisWeek.settlements.map((s) => s.matters);
                        format = data.statistics.matter.futureSettlements.thisWeek.settlements.map((s) => s.format);
                    } else if (state.last === LastCountEnum.PREV_MONTH) {
                        mattersWithoutSettlements = data.statistics.matter.futureSettlements.previousMonth.summaries.mattersWithoutSettlements;
                        total = data.statistics.matter.futureSettlements.previousMonth.summaries.total;
                        mattersSettled = data.statistics.matter.futureSettlements.previousMonth.summaries.mattersSettled;

                        amount = data.statistics.matter.futureSettlements.previousMonth.settlements.map((s) => s.amount);
                        grouping = data.statistics.matter.futureSettlements.previousMonth.settlements.map((s) => s.grouping);
                        matters = data.statistics.matter.futureSettlements.previousMonth.settlements.map((s) => s.matters);
                        format = data.statistics.matter.futureSettlements.previousMonth.settlements.map((s) => s.format);

                        periodString = 'Previous Month';
                    } else if (state.last === LastCountEnum.MONTH) {
                        mattersWithoutSettlements = data.statistics.matter.futureSettlements.thisMonth.summaries.mattersWithoutSettlements;
                        total = data.statistics.matter.futureSettlements.thisMonth.summaries.total;
                        mattersSettled = data.statistics.matter.futureSettlements.thisMonth.summaries.mattersSettled;

                        amount = data.statistics.matter.futureSettlements.thisMonth.settlements.map((s) => s.amount);
                        grouping = data.statistics.matter.futureSettlements.thisMonth.settlements.map((s) => s.grouping);
                        matters = data.statistics.matter.futureSettlements.thisMonth.settlements.map((s) => s.matters);
                        format = data.statistics.matter.futureSettlements.thisMonth.settlements.map((s) => s.format);

                        periodString = 'This Month';
                    } else if (state.last === LastCountEnum.NEXT_MONTH) {
                        mattersWithoutSettlements = data.statistics.matter.futureSettlements.nextMonth.summaries.mattersWithoutSettlements;
                        total = data.statistics.matter.futureSettlements.nextMonth.summaries.total;
                        mattersSettled = data.statistics.matter.futureSettlements.nextMonth.summaries.mattersSettled;

                        amount = data.statistics.matter.futureSettlements.nextMonth.settlements.map((s) => s.amount);
                        grouping = data.statistics.matter.futureSettlements.nextMonth.settlements.map((s) => s.grouping);
                        matters = data.statistics.matter.futureSettlements.nextMonth.settlements.map((s) => s.matters);
                        format = data.statistics.matter.futureSettlements.nextMonth.settlements.map((s) => s.format);

                        periodString = 'Next Month';
                    } else if (state.last === LastCountEnum.NEXT_6_MONTHS) {
                        mattersWithoutSettlements = data.statistics.matter.futureSettlements.nextSixMonths.summaries.mattersWithoutSettlements;
                        total = data.statistics.matter.futureSettlements.nextSixMonths.summaries.total;
                        mattersSettled = data.statistics.matter.futureSettlements.nextSixMonths.summaries.mattersSettled;

                        amount = data.statistics.matter.futureSettlements.nextSixMonths.settlements.map((s) => s.amount);
                        grouping = data.statistics.matter.futureSettlements.nextSixMonths.settlements.map((s) => s.grouping);
                        matters = data.statistics.matter.futureSettlements.nextSixMonths.settlements.map((s) => s.matters);
                        format = data.statistics.matter.futureSettlements.nextSixMonths.settlements.map((s) => s.format);

                        periodString = 'Next 6 Month';
                    } else if (state.last === LastCountEnum.NEXT_12_MONTHS) {
                        mattersWithoutSettlements = data.statistics.matter.futureSettlements.nextTwelveMonths.summaries.mattersWithoutSettlements;
                        total = data.statistics.matter.futureSettlements.nextTwelveMonths.summaries.total;
                        mattersSettled = data.statistics.matter.futureSettlements.nextTwelveMonths.summaries.mattersSettled;

                        amount = data.statistics.matter.futureSettlements.nextTwelveMonths.settlements.map((s) => s.amount);
                        grouping = data.statistics.matter.futureSettlements.nextTwelveMonths.settlements.map((s) => s.grouping);
                        matters = data.statistics.matter.futureSettlements.nextTwelveMonths.settlements.map((s) => s.matters);
                        format = data.statistics.matter.futureSettlements.nextTwelveMonths.settlements.map((s) => s.format);

                        periodString = 'Next 12 Months';
                    } else if (state.last === LastCountEnum.NEXT_3_YEARS) {
                        mattersWithoutSettlements = data.statistics.matter.futureSettlements.nextThreeYears.summaries.mattersWithoutSettlements;
                        total = data.statistics.matter.futureSettlements.nextThreeYears.summaries.total;
                        mattersSettled = data.statistics.matter.futureSettlements.nextThreeYears.summaries.mattersSettled;

                        amount = data.statistics.matter.futureSettlements.nextThreeYears.settlements.map((s) => s.amount);
                        grouping = data.statistics.matter.futureSettlements.nextThreeYears.settlements.map((s) => s.grouping);
                        matters = data.statistics.matter.futureSettlements.nextThreeYears.settlements.map((s) => s.matters);
                        format = data.statistics.matter.futureSettlements.nextThreeYears.settlements.map((s) => s.format);

                        periodString = 'Next 3 Years';
                    }
                }

                return {
                    ...prevState,
                    amount,
                    grouping,
                    matters,
                    periodString,
                    total,
                    mattersWithoutSettlements,
                    format,
                    mattersSettled,
                };
            });
        }

        // tslint:disable-next-line: align
    }, [state.last, loading, error, data]);

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart('isFutureSettlementsChartOpen');
        }
    };

    const onToggleVideo = () => {
        setIsVideoModalOpen(!isVideoModalOpen);
    };

    const onChangeRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prevState) => {
            return {
                ...prevState,
                // tslint:disable-next-line: radix
                last: parseInt((event.target as HTMLInputElement).value),
            };
        });
    };

    const handleDrilldownOnClick = (comparison: 'settlementPeriod' | 'noSettlement' | 'matterSettled') => () => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();

            if (state.last === LastCountEnum.WEEK) {
                searchCriterias.push(
                    {
                        property: 'SevenDays',
                        operator: 'eq',
                        comparison: comparison,
                        period: '',
                    }, //
                );
            } else if (state.last === LastCountEnum.PREV_MONTH || state.last === LastCountEnum.MONTH || state.last === LastCountEnum.NEXT_MONTH) {
                //
                const firstGroup = state.grouping.filter((tempVal: string) => tempVal.length > 0)[0];

                const formatFirstGroup = moment(firstGroup).format('YYYY-MM');

                searchCriterias.push(
                    {
                        property: 'ThirtyDays',
                        operator: 'eq',
                        comparison: comparison,
                        period: formatFirstGroup,
                    }, //
                );
            } else if (state.last === LastCountEnum.NEXT_6_MONTHS) {
                searchCriterias.push(
                    {
                        property: '6MonthPeriod',
                        operator: 'eq',
                        comparison: comparison,
                        period: '',
                    }, //
                );
            } else if (state.last === LastCountEnum.NEXT_12_MONTHS) {
                searchCriterias.push(
                    {
                        property: '12MonthPeriod',
                        operator: 'eq',
                        comparison: comparison,
                        period: '',
                    }, //
                );
            } else if (state.last === LastCountEnum.NEXT_3_YEARS) {
                searchCriterias.push(
                    {
                        property: 'YearsRange',
                        operator: 'eq',
                        comparison: comparison,
                        period: '3',
                    }, //
                );
            }

            props.getSelectedItem(searchCriterias, true, 'Settlements', DrillDownType.matterSummaryDrilldown, 'Settlements');
        }
    };

    const onPan = (context: { chart: Chart }) => {
        chartRef.current.options.scales.y.min = 0;
        chartRef.current.update();
    };

    const onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    };

    if (loading || currencySymbolQuery.loading) {
        return (
            <ChartSkeleton variant="columns" />
        );
    }
    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(state.amount) ? ' rv-chart--empty' : ''}`}>
            {isVideoModalOpen && props.infoUrl && (
                <VideoModal 
                    open={isVideoModalOpen}
                    onClose={onToggleVideo}
                    url={props.infoUrl}
                />
            )}
            <IconButtonWrapper aria-label="close">
                <div
                    className={`${classes.helpIcon} rv-chart-help`} 
                >
                    <ChartInfo 
                        description={props.description}
                        fontSize={props.showAllRecords ? 'large' : 'medium'}
                        // className={classes.helpIcon}
                    />
                    {props.infoUrl && (
                        <MTooltip title="Learn more">
                            <IconButton className={classes.learnIcon} aria-label="maximize" onClick={onToggleVideo} size={props.showAllRecords ? 'medium' : 'small'}>
                                <VideoLibraryOutlinedIcon />
                            </IconButton>
                        </MTooltip>
                    )}
                </div>
                <IconButton aria-label="close" onClick={onCloseChart} size="small">
                    <CloseIcon className={classes.closeIcon} />
                </IconButton>
            </IconButtonWrapper>
            <div className={classes.titleContainer}>
                <ChartHeading icon={<TimelineOutlinedIcon />} title="Settlements" />
                <div className={`${classes.total} h2`} onClick={handleDrilldownOnClick('settlementPeriod')}>
                    <AnimatedNumber value={state.total} format={(amount) => `${state.symbol}${formatNumber(amount)}`} />
                </div>
                <ChartStatTiles
                    tiles={[
                        { label: 'Settled', value: `${formatNumber(state.mattersSettled)} ${state.mattersSettled > 1 ? 'matters' : 'matter'}`, color: RV_CHART_COLORS.green, onClick: handleDrilldownOnClick('settlementPeriod') },
                        { label: 'Without estimates', value: `${formatNumber(state.mattersWithoutSettlements)} ${state.mattersWithoutSettlements > 1 ? 'matters' : 'matter'}`, color: RV_CHART_COLORS.amber, onClick: handleDrilldownOnClick('noSettlement') },
                    ]}
                />
            </div>
            <div className={classes.barContainer}>
                <Bar
                    ref={chartRef}
                    // tslint:disable-next-line: no-any
                    onClick={(event: React.MouseEvent<any>) => {
                        const _data = getElementAtEvent(chartRef.current, event);
                        if (_data.length > 0 && props.getSelectedItem) {
                            const _index = getElementAtEvent(chartRef.current, event)[0].index;

                            // const item = state.format[_index];

                            const selectedGrouping = state.grouping[_index];

                            const searchCriterias = new Array<SearchCriteria>();

                            let dateLabelFormatt = moment(selectedGrouping).format('MMM DD, YYYY');
                            // var d = new Date();
                            // var year = d.getFullYear();

                            // tslint:disable-next-line: max-line-length
                            if (
                                state.last === LastCountEnum.WEEK ||
                                state.last === LastCountEnum.PREV_MONTH ||
                                state.last === LastCountEnum.MONTH ||
                                state.last === LastCountEnum.NEXT_MONTH
                            ) {
                                searchCriterias.push(
                                    {
                                        property: 'PeriodByDays',
                                        operator: 'eq',
                                        comparison: 'settlementDay',
                                        period: selectedGrouping,
                                    }, //
                                );
                            } else if (state.last === LastCountEnum.NEXT_6_MONTHS || state.last === LastCountEnum.NEXT_12_MONTHS) {
                                dateLabelFormatt = moment(selectedGrouping).format('MMM YYYY');

                                searchCriterias.push(
                                    {
                                        property: 'ThirtyDays',
                                        operator: 'eq',
                                        comparison: 'settlementPeriod',
                                        period: selectedGrouping,
                                    }, //
                                );
                            } else if (state.last === LastCountEnum.NEXT_3_YEARS) {
                                dateLabelFormatt = selectedGrouping;

                                searchCriterias.push(
                                    {
                                        property: '12MonthPeriod',
                                        operator: 'eq',
                                        comparison: 'settlementYear',
                                        period: selectedGrouping,
                                    }, //
                                );
                            }

                            props.getSelectedItem(searchCriterias, true, 'Settlements - ' + dateLabelFormatt, DrillDownType.matterSummaryDrilldown, 'Settlements');
                        }
                    }}
                    data={{
                        labels: getLabels(state.format),
                        datasets: [
                            // {
                            //     fill: false,
                            //     label: 'Budget',
                            //     backgroundColor: 'orange',
                            //     borderColor: 'orange',
                            //     borderWidth: 3,
                            //     hoverBackgroundColor: 'orange',
                            //     hoverBorderColor: 'orange',
                            //     data: state.budget,
                            //     type: 'line',
                            //     pointStyle: 'line'
                            // },
                            {
                                fill: false,
                                label: 'Estimated Fees',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primary),
                                data: state.amount,
                                type: 'bar',
                            },
                            // tslint:disable-next-line:no-any
                        ] as any,
                    }}
                    options={{
                        onHover: onHover,
                        indexAxis: 'x' as const,
                        animation: rvChartAnimation(),
                        elements: { bar: { borderWidth: 0 } },
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            y: {
                                grid: rvValueAxisStyle.grid,
                                // beginAtZero: true,
                                suggestedMax: getSuggestedMaxTick([...state.amount]),
                                beginAtZero: true,
                                ticks: {
                                    ...rvValueAxisStyle.ticks,
                                    maxTicksLimit: getMaxTickLimit([...state.amount]),
                                    autoSkip: false,
                                    callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                        return `${state.symbol}${formatNumber(tickValue)}`;
                                    },
                                },
                            },
                            x: {
                                grid: rvCategoryAxisStyle.grid,
                                // min: 0,
                                // max: 9 // minimum display
                                ticks: {
                                    ...rvCategoryAxisStyle.ticks,
                                    autoSkip: false,
                                },
                            },
                        },
                        plugins: {
                            legend: {
                                position: 'bottom' as const,
                                display: false,
                            },
                            title: {
                                display: false,
                                text: ``,
                            },
                            zoom: {
                                pan: {
                                    enabled: false,
                                    onPan: onPan,
                                },
                                zoom: {
                                    wheel: {
                                        enabled: false,
                                    },
                                },
                                // limits: {
                                //   y: { min: 0, max: 9 },
                                // },
                            },
                            tooltip: {
                                ...rvTooltipStyle,
                                callbacks: {
                                    // tslint:disable-next-line: no-any
                                    label: function (context: any) {
                                        const {
                                            //   dataIndex,
                                            //   label,
                                            formattedValue,
                                            dataset,
                                        } = context;

                                        var currentValue = convertStringNumberToNumber(formattedValue);

                                        return `${dataset.label}: ${state.symbol}${formatNumber(currentValue)}`;
                                    },
                                    // tslint:disable-next-line: no-any
                                    afterLabel: (context: any) => {
                                        const {
                                            dataIndex,
                                            //   label,
                                            // formattedValue,
                                            // dataset,
                                        } = context;
                                        // const amount = state.amount[tooltipItem.index];
                                        const matter = state.matters[dataIndex];

                                        return [`Matters: ${formatNumber(matter)}`];
                                    },
                                },
                            },
                        },
                    }}
                />
                {isChartDataEmpty(state.amount) && (
                    <ChartEmptyState title="No data" hint="Nothing to show for this selection." />
                )}
            </div>
            <FormControl component="div">
                <RadioGroup name="last" value={state.last} onChange={onChangeRadio} className={classes.radioGroup}>
                    <FormControlLabel value={LastCountEnum.WEEK} control={<Radio color="primary" />} label="This Week" />
                    <FormControlLabel value={LastCountEnum.MONTH} control={<Radio color="primary" />} label="This Month" />
                    <FormControlLabel value={LastCountEnum.NEXT_MONTH} control={<Radio color="primary" />} label="Next Month" />
                    <FormControlLabel value={LastCountEnum.PREV_MONTH} control={<Radio color="primary" />} label="Previous Month" />
                    <FormControlLabel value={LastCountEnum.NEXT_6_MONTHS} control={<Radio color="primary" />} label="Next 6 Months" />
                    <FormControlLabel value={LastCountEnum.NEXT_12_MONTHS} control={<Radio color="primary" />} label="Next 12 Months" />
                    <FormControlLabel value={LastCountEnum.NEXT_3_YEARS} control={<Radio color="primary" />} label="Next 3 Years" />
                </RadioGroup>
            </FormControl>
        </div>
    );
};

export interface CurrencySymbolData {
    options: Options;
}

export interface Options {
    currencySymbol: CurrencySymbol;
}

export interface CurrencySymbol {
    symbol: string;
}

export const CURRENCY_SYMBOL_QUERY = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;

export interface FutureSettlementData {
    statistics: Statistics;
}

export interface Statistics {
    matter: Matter;
}

export interface Matter {
    futureSettlements: FutureSettlements;
}

export interface FutureSettlements {
    thisWeek: NextMonth;
    thisMonth: NextMonth;
    previousMonth: NextMonth;
    nextMonth: NextMonth;
    nextSixMonths: NextMonth;
    nextTwelveMonths: NextMonth;
    nextThreeYears: NextMonth;
}

export interface NextMonth {
    settlements: Settlement[];
    summaries: Summaries;
}

export interface Settlement {
    amount: number;
    grouping: string;
    matters: number;
    format: string;
}

export interface Summaries {
    mattersWithoutSettlements: number;
    total: number;
    mattersSettled: number;
}

const FUTURE_SETTLEMENTS_CHART_QYERY = gql`
    query FutureSettlements($entityType: Int, $entityGuid: String, $responsibility: Int) {
        statistics {
            matter {
                futureSettlements(entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility) {
                    thisWeek {
                        settlements {
                            amount
                            grouping
                            matters
                            format
                        }
                        summaries {
                            mattersWithoutSettlements
                            total
                            mattersSettled
                        }
                    }
                    thisMonth {
                        settlements {
                            amount
                            grouping
                            matters
                            format
                        }
                        summaries {
                            mattersWithoutSettlements
                            total
                            mattersSettled
                        }
                    }
                    previousMonth {
                        settlements {
                            amount
                            grouping
                            matters
                            format
                        }
                        summaries {
                            mattersWithoutSettlements
                            total
                            mattersSettled
                        }
                    }
                    nextMonth {
                        settlements {
                            amount
                            grouping
                            matters
                            format
                        }
                        summaries {
                            mattersWithoutSettlements
                            total
                            mattersSettled
                        }
                    }
                    nextSixMonths {
                        settlements {
                            amount
                            grouping
                            matters
                            format
                        }
                        summaries {
                            mattersWithoutSettlements
                            total
                            mattersSettled
                        }
                    }
                    nextTwelveMonths {
                        settlements {
                            amount
                            grouping
                            matters
                            format
                        }
                        summaries {
                            mattersWithoutSettlements
                            total
                            mattersSettled
                        }
                    }
                    nextThreeYears {
                        settlements {
                            amount
                            grouping
                            matters
                            format
                        }
                        summaries {
                            mattersWithoutSettlements
                            total
                            mattersSettled
                        }
                    }
                }
            }
        }
    }
`;
